import type { OffPlatformContractInput, ProgressPayment, Variation } from '../types';
import type { Attachment } from '@paid-ui/schemas/zod/attachment';
import type { ContractSnapshot, LiquidatedDamages } from '@paid-ui/types/contracts';

import { groupBy, isNil } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { PaymentStatus, ProgressPaymentTag } from '@paid-ui/enums/payment';
import { isNotNil } from '@paid-ui/utils';
import { utc2MelStr } from '@paid-ui/utils/datetime';

import { UiClaimStatus } from '../models/enums';

/**
 * Fix missing fields in contract snapshot from server side
 *
 * @param snapshot - Contract snapshot from server side
 * @returns - Fixed contract snapshot
 */
export function fixSnapshot(snapshot: ContractSnapshot<any>) {
  // 1. Add missing confirmEmail field in contract snapshot
  if ('project' in snapshot.snapshot && 'homeOwners' in snapshot.snapshot.project) {
    const homeOwners = [...snapshot.snapshot.project.homeOwners];
    snapshot.snapshot.project.homeOwners = homeOwners.map((owner) => ({
      ...owner,
      confirmEmail: owner.email,
    }));
  }

  // 2. Add uiStatus and uiReference in progressPayments
  if ('progressPayments' in snapshot.snapshot && 'claims' in snapshot.snapshot) {
    const claims = [...snapshot.snapshot.claims];
    const progressPayments = [...snapshot.snapshot.progressPayments] as ProgressPayment[];

    snapshot.snapshot.progressPayments = progressPayments.map((payment, index) => {
      let uiStatus = UiClaimStatus.UNCLAIMED;
      const hasClaim = claims.some((claim) => claim.progressPaymentIndex === index);

      if (hasClaim) {
        uiStatus =
          payment.progressPaymentStatus === PaymentStatus.COMPLETED
            ? UiClaimStatus.PAID
            : UiClaimStatus.CLAIMED_UNPAID;
      }

      return {
        ...payment,
        uiStatus,
        uiIsDeposit: payment.tags?.includes(ProgressPaymentTag.DEPOSIT),
        uiReference: payment.id ?? uuidv4(),
      };
    });

    // 2.1 Add claimId in variations
    if ('variations' in snapshot.snapshot) {
      const variations = [...snapshot.snapshot.variations] as Variation[];
      snapshot.snapshot.variations = variations.map((variation) => {
        const targetClaim = isNotNil(variation.claimIndex) ? claims[variation.claimIndex] : null;
        if (!targetClaim) return variation;
        const { progressPaymentIndex } = targetClaim;
        const targetProgressPayment = progressPayments[progressPaymentIndex];
        if (!targetProgressPayment) return variation;

        return {
          ...variation,
          claimId: targetProgressPayment.id ?? '0',
        };
      });
    }
  }

  // 3. Format commencementDate into valid MEL date string
  if ('commencementDate' in snapshot.snapshot) {
    const { commencementDate } = snapshot.snapshot;
    snapshot.snapshot.commencementDate = commencementDate ? utc2MelStr(commencementDate) : '';
  }

  // 4. Group by attachments by category
  if ('attachments' in snapshot.snapshot) {
    const attachments = snapshot.snapshot.attachments as Attachment[];
    const { OFF_PLATFORM = [], LOAN_APPROVAL = [] } = groupBy(
      attachments,
      (attachment) => attachment.category,
    );
    snapshot.snapshot.attachments = {
      LOAN_APPROVAL,
      OFF_PLATFORM,
    };
  }

  // 5. Mapping liquidatedDamages to lateCompletion and ownerCausedDelays
  if ('liquidatedDamages' in snapshot.snapshot) {
    const { lateCompletion, ownerCausedDelays } = snapshot.snapshot
      .liquidatedDamages as LiquidatedDamages;
    snapshot.snapshot.lateCompletion = lateCompletion ?? '';
    snapshot.snapshot.ownerCausedDelays = ownerCausedDelays ?? '';
  }

  return snapshot;
}

/**
 * Get latest contract snapshot in contract.
 *
 * @param snapshots - All contract snapshots from server side
 * @returns Latest contract snapshot or null
 */
export function getLatestContractSnapshot<T extends Record<string, any> = OffPlatformContractInput>(
  snapshots: Record<string, ContractSnapshot<T>[]>,
) {
  const ss = Object.values(snapshots).flat();
  const [s] = ss.toSorted((a, b) => b.snapshotVersion - a.snapshotVersion);
  return s ?? null;
}

/**
 * Get latest contract snapshot with user ID.
 *
 * @param snapshots - All contract snapshots from server side
 * @param options - Options for formatter
 * @param options.userId - The user ID
 * @param options.shouldFix - Fix missing fields in contract snapshot
 * @param options.snapshotVersions - Set of reviewed snapshot versions
 * @returns Latest contract snapshot or null
 */
export function getContractSnapshotWithUserId<
  T extends Record<string, any> = OffPlatformContractInput,
>(
  snapshots: Record<string, ContractSnapshot<T>[]>,
  options: {
    userId?: string;
    shouldFix?: boolean;
    snapshotVersions?: Set<number>;
  },
): ContractSnapshot<T> | null {
  const { userId, shouldFix, snapshotVersions } = options;
  if (!userId) return null;
  const ss = snapshots[userId] ?? [];
  const [s] = ss.toSorted((a, b) => b.snapshotVersion - a.snapshotVersion);
  if (!s) return null;
  if (snapshotVersions && !snapshotVersions.has(s.snapshotVersion)) return null;
  return shouldFix ? fixSnapshot(s) : s;
}

/**
 * Get latest contract snapshot with party ID.
 *
 * @param snapshots - All contract snapshots from server side
 * @param options - Options for formatter
 * @param options.partyId - The party ID
 * @param options.shouldFix - Fix missing fields in contract snapshot
 * @param options.snapshotVersions - Set of reviewed snapshot versions
 * @returns Latest contract snapshot or null
 */
export function getContractSnapshotWithPartyId<
  T extends Record<string, any> = OffPlatformContractInput,
>(
  snapshots: Record<string, ContractSnapshot<T>[]>,
  options: {
    partyId?: string;
    shouldFix?: boolean;
    snapshotVersions?: Set<number>;
  },
): ContractSnapshot<T> | null {
  const { partyId, shouldFix, snapshotVersions } = options;
  if (!partyId) return null;
  const ss = snapshots[partyId] ?? [];
  const [s] = ss.toSorted((a, b) => b.snapshotVersion - a.snapshotVersion);
  if (!s) return null;
  if (snapshotVersions && !snapshotVersions.has(s.snapshotVersion)) return null;
  return shouldFix ? fixSnapshot(s) : s;
}

/**
 * Get field value from snapshot
 *
 * @param snapshot - Snapshot data
 * @param name - Name path
 */
export function getFieldValueFromSnapshot(snapshot: any, name?: string | string[]): any {
  if (name === undefined || name.length === 0) {
    return snapshot;
  }

  const [currentName, ...restNames] = Array.isArray(name) ? name : name.split('.');

  if (currentName === undefined) {
    return snapshot;
  }

  if (currentName === '*') {
    return Array.isArray(snapshot)
      ? snapshot.map((s) => {
          return getFieldValueFromSnapshot(s, restNames);
        })
      : [];
  }

  if (
    isNil(snapshot) ||
    typeof snapshot === 'string' ||
    typeof snapshot === 'number' ||
    typeof snapshot === 'boolean'
  ) {
    return snapshot;
  }

  if (Array.isArray(snapshot)) {
    const index = Number.parseInt(currentName, 10);
    if (Number.isNaN(index)) {
      throw new TypeError(`Invalid array index ${currentName}`);
    }
    return getFieldValueFromSnapshot(snapshot[index], restNames);
  }

  return getFieldValueFromSnapshot(snapshot[currentName], restNames);
}
